/**
 * In-browser stand-in for the Functions backend so the KR-automation dialog can be
 * exercised end-to-end before `functions-kr-only` is deployed.
 *
 * Activated when:
 *   1. `localStorage.krAutomationDemo === 'on'`, OR
 *   2. `localStorage.krAutomationDemo` is unset AND `process.env.NODE_ENV !== 'production'`
 *      AND `process.env.VUE_APP_KR_AUTOMATION_API_URL` is empty (no backend wired up).
 *
 * Set `localStorage.krAutomationDemo = 'off'` to bypass and hit the real Functions.
 *
 * Connections "made" in demo mode persist in `localStorage.krAutomationDemoConnections`
 * so the dialog stays connected across reloads.
 */

import {
  CompiledRecipe,
  ComposeRecipeResponse,
  PreviewResponse,
  ConnectedAccount,
  Toolkit,
  MetricSourcePayload,
} from './metricSourceService';

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const STORAGE_KEY = 'krAutomationDemoConnections';

// Brand logos for the demo backend. Production gets `logo_url` directly from
// `composio.toolkits.list()`; this map only matters when running against the
// demo (and as a frontend-side hint for the recipe-flow / detected-pill where
// Composio's URL hasn't been fetched yet).
//
// We pick from two CDNs:
//   - Simple Icons (cdn.simpleicons.org) — brand-correct SVGs in brand colors,
//     but their catalog removes brands periodically (Slack, Salesforce, Monday
//     have all been removed at some point). Where SI 404s, fall back to Iconify.
//   - Iconify (api.iconify.design) — `logos:` icon set, much wider coverage,
//     same one-URL semantic.
//
// Verified against the live CDNs 2026-05; if a CDN drops a logo, the component
// silently falls through to the monogram via the @error handler.
type LogoSource = { kind: 'simpleicons'; slug: string } | { kind: 'iconify'; icon: string };

const TOOLKIT_LOGOS: Record<string, LogoSource> = {
  hubspot:          { kind: 'simpleicons', slug: 'hubspot' },
  linear:           { kind: 'simpleicons', slug: 'linear' },
  stripe:           { kind: 'simpleicons', slug: 'stripe' },
  github:           { kind: 'simpleicons', slug: 'github' },
  notion:           { kind: 'simpleicons', slug: 'notion' },
  google_sheets:    { kind: 'simpleicons', slug: 'googlesheets' },
  google_analytics: { kind: 'simpleicons', slug: 'googleanalytics' },
  mixpanel:         { kind: 'simpleicons', slug: 'mixpanel' },
  jira:             { kind: 'simpleicons', slug: 'jira' },
  zendesk:          { kind: 'simpleicons', slug: 'zendesk' },
  intercom:         { kind: 'simpleicons', slug: 'intercom' },
  discord:          { kind: 'simpleicons', slug: 'discord' },
  gitlab:           { kind: 'simpleicons', slug: 'gitlab' },
  sentry:           { kind: 'simpleicons', slug: 'sentry' },
  pagerduty:        { kind: 'simpleicons', slug: 'pagerduty' },
  quickbooks:       { kind: 'simpleicons', slug: 'quickbooks' },
  xero:             { kind: 'simpleicons', slug: 'xero' },
  airtable:         { kind: 'simpleicons', slug: 'airtable' },
  asana:            { kind: 'simpleicons', slug: 'asana' },
  trello:           { kind: 'simpleicons', slug: 'trello' },
  helpscout:        { kind: 'simpleicons', slug: 'helpscout' },

  // Simple Icons doesn't have these — use Iconify's `logos:` set instead.
  salesforce:       { kind: 'iconify', icon: 'logos:salesforce' },
  pipedrive:        { kind: 'iconify', icon: 'logos:pipedrive' },
  close:            { kind: 'iconify', icon: 'logos:close' },
  chargebee:        { kind: 'iconify', icon: 'logos:chargebee' },
  amplitude:        { kind: 'iconify', icon: 'logos:amplitude-icon' },
  segment:          { kind: 'iconify', icon: 'logos:segment-icon' },
  monday:           { kind: 'iconify', icon: 'logos:monday-icon' },
  slack:            { kind: 'iconify', icon: 'logos:slack-icon' },
  teams:            { kind: 'iconify', icon: 'logos:microsoft-teams' },
};

export const toolkitLogoUrl = (slug: string): string | undefined => {
  const e = TOOLKIT_LOGOS[slug];
  if (!e) return undefined;
  if (e.kind === 'simpleicons') return `https://cdn.simpleicons.org/${e.slug}`;
  return `https://api.iconify.design/${e.icon}.svg`;
};

// Back-compat alias — older imports still reach for `simpleIconUrl`.
export const simpleIconUrl = toolkitLogoUrl;

const tk = (slug: string, name: string): Toolkit => ({
  slug,
  name,
  has_query_actions: true,
  logo_url: simpleIconUrl(slug),
});

export const DEMO_TOOLKITS: Toolkit[] = [
  tk('hubspot', 'HubSpot'),
  tk('linear', 'Linear'),
  tk('stripe', 'Stripe'),
  tk('github', 'GitHub'),
  tk('notion', 'Notion'),
  tk('google_sheets', 'Google Sheets'),
  tk('google_analytics', 'Google Analytics'),
  tk('mixpanel', 'Mixpanel'),
  tk('salesforce', 'Salesforce'),
  tk('jira', 'Jira'),
  tk('zendesk', 'Zendesk'),
  tk('intercom', 'Intercom'),
];

const TOOLKIT_BY_SLUG: Record<string, Toolkit> = DEMO_TOOLKITS.reduce(
  (acc, t) => {
    acc[t.slug] = t;
    return acc;
  },
  {} as Record<string, Toolkit>
);

/* ---------- demo-mode flag ---------- */

export const isDemoMode = (): boolean => {
  try {
    const flag = window.localStorage.getItem('krAutomationDemo');
    if (flag === 'on') return true;
    if (flag === 'off') return false;
    // Auto-on in dev when no backend is configured.
    const isDev = process.env.NODE_ENV !== 'production';
    const hasBackend = !!process.env.VUE_APP_KR_AUTOMATION_API_URL;
    return isDev && !hasBackend;
  } catch {
    return false;
  }
};

/* ---------- toolkit detection from free-form prompt ---------- */

const TOOLKIT_ALIASES: Array<{ slug: string; patterns: RegExp[] }> = [
  { slug: 'hubspot', patterns: [/\bhubspot\b/i, /\bdeal(s)?\b/i, /\bpipeline\b/i, /\bcrm\b/i, /\bclosed?[\s-]?won\b/i] },
  { slug: 'linear', patterns: [/\blinear\b/i, /\bissue(s)?\b/i, /\bticket(s)?\b/i, /\bsprint\b/i] },
  { slug: 'stripe', patterns: [/\bstripe\b/i, /\bmrr\b/i, /\barr\b/i, /\brevenue\b/i, /\bcharge(s)?\b/i, /\bsubscription(s)?\b/i, /\bpayment(s)?\b/i] },
  { slug: 'github', patterns: [/\bgithub\b/i, /\bpr(s)?\b/i, /\bpull[\s-]?request(s)?\b/i, /\bcommit(s)?\b/i, /\brepo(s)?\b/i] },
  { slug: 'notion', patterns: [/\bnotion\b/i] },
  { slug: 'google_sheets', patterns: [/\bgoogle\s*sheets?\b/i, /\bsheets?\b/i, /\bspreadsheet(s)?\b/i] },
  { slug: 'google_analytics', patterns: [/\bgoogle\s*analytics\b/i, /\bga4\b/i, /\bactive\s*users?\b/i, /\bdaily\s*active\b/i, /\bmonthly\s*active\b/i] },
  { slug: 'mixpanel', patterns: [/\bmixpanel\b/i, /\bevents?\s*tracked\b/i] },
  { slug: 'salesforce', patterns: [/\bsalesforce\b/i, /\bsfdc\b/i] },
  { slug: 'jira', patterns: [/\bjira\b/i, /\bepic(s)?\b/i] },
  { slug: 'zendesk', patterns: [/\bzendesk\b/i, /\bticket(s)?\b/i, /\bsupport\b/i] },
  { slug: 'intercom', patterns: [/\bintercom\b/i, /\bconversations?\b/i] },
];

export const detectToolkitFromPrompt = (prompt: string): Toolkit | null => {
  if (!prompt) return null;
  for (const a of TOOLKIT_ALIASES) {
    if (a.patterns.some((p) => p.test(prompt))) return TOOLKIT_BY_SLUG[a.slug] || null;
  }
  return null;
};

/* ---------- canned recipes per toolkit ---------- */

const recipeFor = (toolkitSlug: string, prompt: string): CompiledRecipe => {
  const lower = (prompt || '').toLowerCase();
  switch (toolkitSlug) {
    case 'hubspot':
      return {
        action_slug: 'HUBSPOT_SEARCH_DEALS',
        action_args: {
          filterGroups: [{ filters: [{ propertyName: 'dealstage', operator: 'EQ', value: 'closedwon' }] }],
          properties: ['amount', 'dealname', 'closedate'],
          limit: 100,
        },
        post_processing: {
          result_path: '$.results',
          aggregation: 'sum',
          value_field: 'properties.amount',
          date_field: 'properties.closedate',
          currency: 'THB',
        },
      };
    case 'linear':
      return {
        action_slug: 'LINEAR_LIST_ISSUES',
        action_args: { state: 'completed', limit: 100 },
        post_processing: {
          result_path: '$.issues',
          aggregation: 'count',
          date_field: 'completedAt',
        },
      };
    case 'stripe': {
      const isMrr = /mrr|recurring|subscription/.test(lower);
      return {
        action_slug: isMrr ? 'STRIPE_LIST_SUBSCRIPTIONS' : 'STRIPE_LIST_CHARGES',
        action_args: { limit: 100, status: isMrr ? 'active' : 'succeeded' },
        post_processing: {
          result_path: '$.data',
          aggregation: 'sum',
          value_field: isMrr ? 'plan.amount' : 'amount',
          date_field: 'created',
          currency: 'usd',
        },
      };
    }
    case 'github':
      return {
        action_slug: 'GITHUB_LIST_PULL_REQUESTS',
        action_args: { state: 'closed', per_page: 100 },
        post_processing: {
          result_path: '$',
          aggregation: 'count',
          date_field: 'merged_at',
        },
      };
    case 'google_analytics':
      return {
        action_slug: 'GA4_RUN_REPORT',
        action_args: { metrics: ['activeUsers'], dateRange: { startDate: '30daysAgo', endDate: 'today' } },
        post_processing: {
          result_path: '$.rows',
          aggregation: 'sum',
          value_field: 'metricValues[0].value',
        },
      };
    case 'google_sheets':
      return {
        action_slug: 'GOOGLE_SHEETS_GET_VALUES',
        action_args: { range: 'Sheet1!A2:B' },
        post_processing: {
          result_path: '$.values',
          aggregation: 'sum',
          value_field: '[1]',
        },
      };
    default:
      return {
        action_slug: `${toolkitSlug.toUpperCase()}_LIST`,
        action_args: { limit: 100 },
        post_processing: {
          result_path: '$.results',
          aggregation: 'count',
        },
      };
  }
};

const humanSummaryFor = (toolkit: Toolkit, prompt: string): string => {
  const window = /this\s+(week|quarter|month|year)/i.exec(prompt || '');
  const range = window ? `this ${window[1].toLowerCase()}` : 'over the last 30 days';
  switch (toolkit.slug) {
    case 'hubspot':
      return `Sum the amount of every deal that reached "Closed Won" in HubSpot ${range}, in THB.`;
    case 'linear':
      return `Count every Linear issue that moved to a completed state ${range}.`;
    case 'stripe':
      return `Sum all successful Stripe charges ${range}, converted to your KR's currency.`;
    case 'github':
      return `Count every pull request merged to the default branch ${range}.`;
    case 'google_analytics':
      return `Sum daily active users from Google Analytics ${range}.`;
    case 'google_sheets':
      return `Sum the second column of "Sheet1" ${range}.`;
    default:
      return `Pull records from ${toolkit.name} and aggregate them ${range}.`;
  }
};

const clarificationsFor = (prompt: string): string[] => {
  const out: string[] = [];
  if (!/this\s+(week|quarter|month|year)|last\s+\d+\s+day|past\s+\d+\s+day/i.test(prompt || '')) {
    out.push('Which date range should I use?');
  }
  if (!/thb|usd|sgd|eur|currency/i.test(prompt || '') && /amount|value|revenue|deal|stripe|hubspot/i.test(prompt || '')) {
    out.push('Which currency?');
  }
  if (!/team|department|owner|cohort|segment/i.test(prompt || '') && /deal|issue|ticket/i.test(prompt || '')) {
    out.push('Filter by team or owner?');
  }
  return out.slice(0, 3);
};

/* ---------- in-storage connections ---------- */

interface DemoConnection extends ConnectedAccount {
  composio_connected_account_id: string;
}

const loadConnections = (): DemoConnection[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveConnections = (list: DemoConnection[]) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
};

const ensureConnection = (toolkitSlug: string): DemoConnection => {
  const all = loadConnections();
  const existing = all.find((c) => c.toolkit_slug === toolkitSlug && c.status === 'active');
  if (existing) return existing;
  const tk = TOOLKIT_BY_SLUG[toolkitSlug];
  const id = `ca_demo_${toolkitSlug}_${Date.now().toString(36)}`;
  const fresh: DemoConnection = {
    id,
    composio_connected_account_id: id,
    toolkit_slug: toolkitSlug,
    display_label: `${tk ? tk.name : toolkitSlug} demo workspace`,
    status: 'active',
    connected_by_email: 'demo@happily.ai',
    created_at: new Date().toISOString(),
  };
  saveConnections([...all, fresh]);
  return fresh;
};

const removeConnection = (accountId: string) => {
  const all = loadConnections().filter((c) => c.id !== accountId);
  saveConnections(all);
};

/* ---------- preview value generators ---------- */

const seededValue = (seed: string, min: number, max: number): number => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return min + (h % (max - min));
};

const HUBSPOT_DEAL_NAMES = [
  'Acme Corp · Annual',
  'Northwind Traders · Q2 expansion',
  'Globex · Pilot upsell',
  'Initech · Renewal',
  'Stark Industries · Enterprise tier',
];

const LINEAR_ISSUE_TITLES = [
  'Onboarding flow polish',
  'Search index migration',
  'Notifications timezone fix',
  'Dashboard chart refresh',
  'Webhook retry logic',
];

const STRIPE_CUSTOMER_NAMES = [
  'cus_bright_panda',
  'cus_silent_oak',
  'cus_polar_lake',
  'cus_velvet_iris',
  'cus_rapid_meadow',
];

const GH_PR_TITLES = [
  'feat(auth): SSO discovery flow',
  'fix(charts): tooltip overlap on small screens',
  'chore(deps): bump axios to 1.7',
  'docs: clarify cohort semantics',
  'refactor(store): split goals module',
];

const sampleRecordsFor = (
  toolkitSlug: string,
  seed: string,
  totalRecords: number,
  totalValue: number
): Array<{ label: string; value: string }> => {
  const offset = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 17 + s.charCodeAt(i)) >>> 0;
    return h;
  };
  const o = offset(seed);
  switch (toolkitSlug) {
    case 'hubspot':
      return [0, 1, 2].map((i) => ({
        label: HUBSPOT_DEAL_NAMES[(o + i) % HUBSPOT_DEAL_NAMES.length],
        value: `THB ${Math.floor(totalValue / Math.max(totalRecords, 1) + (i * 12500)).toLocaleString()}`,
      }));
    case 'linear':
      return [0, 1, 2].map((i) => ({
        label: LINEAR_ISSUE_TITLES[(o + i) % LINEAR_ISSUE_TITLES.length],
        value: 'Done',
      }));
    case 'stripe':
      return [0, 1, 2].map((i) => ({
        label: STRIPE_CUSTOMER_NAMES[(o + i) % STRIPE_CUSTOMER_NAMES.length],
        value: `$${(Math.floor(totalValue / 100 / Math.max(totalRecords, 1)) + i * 5).toLocaleString()}`,
      }));
    case 'github':
      return [0, 1, 2].map((i) => ({
        label: GH_PR_TITLES[(o + i) % GH_PR_TITLES.length],
        value: 'merged',
      }));
    case 'google_analytics':
      return [0, 1, 2].map((i) => ({
        label: `Day ${i + 1}`,
        value: `${(Math.floor(totalValue / Math.max(totalRecords, 1)) + i * 25).toLocaleString()} users`,
      }));
    default:
      return [0, 1, 2].map((i) => ({
        label: `Record ${i + 1}`,
        value: '',
      }));
  }
};

const previewFor = (
  toolkitSlug: string,
  recipe: CompiledRecipe,
  krContext: { title?: string; unit?: string; target_value?: number },
  regenerate: boolean
): PreviewResponse => {
  const seed = JSON.stringify(recipe) + (regenerate ? Date.now().toString() : '');
  let value: number;
  let records: number;
  let note: string;
  switch (toolkitSlug) {
    case 'hubspot':
      records = seededValue(seed, 12, 38);
      value = seededValue(seed, 1_200_000, 4_500_000);
      note = `${records} deals closed won this period (THB ${value.toLocaleString()} added to pipeline).`;
      break;
    case 'linear':
      records = seededValue(seed, 18, 64);
      value = records;
      note = `Team shipped ${records} Linear issues this period.`;
      break;
    case 'stripe':
      records = seededValue(seed, 60, 220);
      value = seededValue(seed, 35_000, 120_000);
      note = `${records} successful Stripe charges totalling $${(value / 100).toLocaleString()}.`;
      break;
    case 'github':
      records = seededValue(seed, 8, 42);
      value = records;
      note = `${records} pull requests merged this period.`;
      break;
    case 'google_analytics':
      records = seededValue(seed, 1, 30);
      value = seededValue(seed, 800, 3500);
      note = `Active users averaged ${value.toLocaleString()} per day across ${records} days of data.`;
      break;
    default:
      records = seededValue(seed, 5, 40);
      value = records;
      note = `Pulled ${records} records from ${toolkitSlug}.`;
  }
  return {
    value,
    sample_note: note,
    source_summary: {
      records_counted: records,
      date_range: { start: '2026-04-03', end: '2026-05-02' },
      filters_applied: Object.keys(recipe.action_args || {}),
      sample_records: sampleRecordsFor(toolkitSlug, seed, records, value),
    },
    warnings: [],
  };
};

/* ---------- public API used by metricSourceService.ts ---------- */

export const demoComposeMetricRecipe = async (params: {
  prompt: string;
  kr_context: { title?: string; unit?: string; target_value?: number };
  toolkit_hint?: string;
}): Promise<ComposeRecipeResponse> => {
  await sleep(650);
  const slug = params.toolkit_hint || (detectToolkitFromPrompt(params.prompt) || TOOLKIT_BY_SLUG.hubspot).slug;
  const toolkit = TOOLKIT_BY_SLUG[slug] || TOOLKIT_BY_SLUG.hubspot;
  return {
    recipe: recipeFor(toolkit.slug, params.prompt),
    human_summary: humanSummaryFor(toolkit, params.prompt),
    suggested_toolkit_slug: toolkit.slug,
    suggested_toolkit_name: toolkit.name,
    needs_clarification: clarificationsFor(params.prompt),
  };
};

export const demoPreviewMetricRecipe = async (params: {
  recipe: CompiledRecipe;
  connected_account_id?: string;
  toolkit_slug?: string;
  kr_context: { title?: string; unit?: string; target_value?: number };
  regenerate_note?: boolean;
}): Promise<PreviewResponse> => {
  await sleep(800);
  const all = loadConnections();
  const conn = all.find((c) => c.id === params.connected_account_id);
  const slug = params.toolkit_slug || (conn ? conn.toolkit_slug : 'hubspot');
  return previewFor(slug, params.recipe, params.kr_context, !!params.regenerate_note);
};

export const demoSaveKrMetricSource = async (
  _params: { company_id: string; goal_id: string; kr_id: string; metric_source: MetricSourcePayload }
): Promise<{ ok: boolean; next_run_at: string }> => {
  await sleep(450);
  return { ok: true, next_run_at: new Date(Date.now() + 24 * 3600 * 1000).toISOString() };
};

export const demoRunKrNow = async (
  _params: { company_id: string; goal_id: string; kr_id: string }
): Promise<{ accepted: boolean }> => {
  await sleep(300);
  return { accepted: true };
};

export const demoListConnectedAccounts = async (params?: {
  toolkit_slug?: string;
}): Promise<{ accounts: ConnectedAccount[] }> => {
  await sleep(180);
  let all: ConnectedAccount[] = loadConnections().filter((c) => c.status !== 'revoked');
  if (params && params.toolkit_slug) {
    all = all.filter((c) => c.toolkit_slug === params.toolkit_slug);
  }
  return { accounts: all };
};

export const demoStartConnectAccount = async (params: {
  toolkit_slug: string;
}): Promise<{ redirect_url: string; state_token: string }> => {
  await sleep(220);
  const stateToken = `demo_state_${params.toolkit_slug}_${Date.now().toString(36)}`;
  // Build a self-target URL the dialog opens — completeDemoConnection runs after
  // the dialog calls demoCompleteConnection() to finalize.
  return {
    redirect_url: `about:blank#kr-demo-${params.toolkit_slug}`,
    state_token: stateToken,
  };
};

export const demoCompleteConnection = async (toolkitSlug: string): Promise<DemoConnection> => {
  await sleep(1500);
  return ensureConnection(toolkitSlug);
};

export const demoRevokeConnectedAccount = async (params: {
  account_id: string;
}): Promise<{ ok: boolean }> => {
  await sleep(250);
  removeConnection(params.account_id);
  return { ok: true };
};

export const demoListToolkits = async (params?: {
  q?: string;
}): Promise<{ toolkits: Toolkit[] }> => {
  await sleep(120);
  if (!params || !params.q) return { toolkits: DEMO_TOOLKITS };
  const q = params.q.toLowerCase();
  return {
    toolkits: DEMO_TOOLKITS.filter(
      (t) => t.slug.includes(q) || t.name.toLowerCase().includes(q)
    ),
  };
};

export const clearDemoConnections = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

/* =====================================================================
 *  Sync runs (Activity feed)
 *
 *  Mirrors a slim subset of the production `sync_runs` doc shape that the
 *  UI actually needs. Production fills these from schedule-nest; the demo
 *  backend seeds a plausible 7-day window the first time the Activity
 *  tab is opened so reviewers see real-feeling data.
 * ===================================================================== */

const SYNC_RUNS_KEY = 'krAutomationDemoSyncRuns';

export type SyncRunStatus =
  | 'success'
  | 'no_change'
  | 'error'
  | 'auth_expired'
  | 'zero_records';

export interface SyncRun {
  id: string;
  toolkit_slug: string;
  connected_account_id?: string;
  kr_id: string;
  kr_title: string;
  goal_title: string;
  trigger: 'scheduled' | 'manual' | 'preview';
  status: SyncRunStatus;
  previous_value?: number;
  new_value?: number;
  unit?: string;
  generated_note?: string;
  error?: { code: string; message: string };
  records_counted?: number;
  started_at: string;        // ISO
  duration_ms: number;
}

const SAMPLE_SYNC_TEMPLATES: Array<Omit<SyncRun, 'id' | 'started_at' | 'duration_ms'>> = [
  {
    toolkit_slug: 'hubspot',
    kr_id: 'kr_demo_pipeline_q2',
    kr_title: 'Closed-won pipeline value',
    goal_title: 'Hit Q2 revenue target',
    trigger: 'scheduled',
    status: 'success',
    previous_value: 1620000,
    new_value: 1845000,
    unit: 'THB',
    generated_note: 'HubSpot pipeline grew by THB 225,000 since the last refresh (7 new closed-won deals).',
    records_counted: 24,
  },
  {
    toolkit_slug: 'google_analytics',
    kr_id: 'kr_demo_active_users',
    kr_title: 'Daily active users',
    goal_title: 'Grow engaged user base',
    trigger: 'scheduled',
    status: 'error',
    error: { code: 'rate_limited', message: 'Google Analytics returned 429. Backing off and retrying on the next tick.' },
    records_counted: 0,
  },
  {
    toolkit_slug: 'stripe',
    kr_id: 'kr_demo_mrr',
    kr_title: 'Monthly recurring revenue',
    goal_title: 'Reach USD 250k MRR',
    trigger: 'scheduled',
    status: 'no_change',
    previous_value: 218400,
    new_value: 218400,
    unit: 'USD',
    records_counted: 312,
  },
  {
    toolkit_slug: 'github',
    kr_id: 'kr_demo_prs_merged',
    kr_title: 'Pull requests merged to main',
    goal_title: 'Improve engineering velocity',
    trigger: 'scheduled',
    status: 'success',
    previous_value: 92,
    new_value: 104,
    generated_note: 'Twelve more pull requests merged this week. Velocity is trending up.',
    records_counted: 104,
  },
  {
    toolkit_slug: 'linear',
    kr_id: 'kr_demo_issues_completed',
    kr_title: 'Issues completed by Platform team',
    goal_title: 'Ship reliability roadmap',
    trigger: 'scheduled',
    status: 'success',
    previous_value: 38,
    new_value: 41,
    generated_note: 'Three more Linear issues moved to Done. Platform team is on pace for the sprint goal.',
    records_counted: 41,
  },
  {
    toolkit_slug: 'linear',
    kr_id: 'kr_demo_bug_burn',
    kr_title: 'Open P1 bugs',
    goal_title: 'Burn down P1 backlog',
    trigger: 'manual',
    status: 'success',
    previous_value: 14,
    new_value: 11,
    generated_note: 'Three P1 bugs were resolved by the Platform team since the last refresh.',
    records_counted: 11,
  },
  {
    toolkit_slug: 'stripe',
    kr_id: 'kr_demo_failed_charges',
    kr_title: 'Failed charge rate',
    goal_title: 'Reduce payment failures',
    trigger: 'scheduled',
    status: 'auth_expired',
    error: { code: 'auth_expired', message: 'Stripe access token expired. Reconnect the app to resume syncs.' },
  },
  {
    toolkit_slug: 'hubspot',
    kr_id: 'kr_demo_demos_booked',
    kr_title: 'Sales demos booked',
    goal_title: 'Top of funnel growth',
    trigger: 'scheduled',
    status: 'success',
    previous_value: 47,
    new_value: 52,
    generated_note: 'Five new demos booked since the last refresh, the strongest week so far this quarter.',
    records_counted: 52,
  },
  {
    toolkit_slug: 'github',
    kr_id: 'kr_demo_release_count',
    kr_title: 'Production releases shipped',
    goal_title: 'Improve release cadence',
    trigger: 'scheduled',
    status: 'zero_records',
    records_counted: 0,
  },
  {
    toolkit_slug: 'linear',
    kr_id: 'kr_demo_issues_completed',
    kr_title: 'Issues completed by Platform team',
    goal_title: 'Ship reliability roadmap',
    trigger: 'scheduled',
    status: 'success',
    previous_value: 35,
    new_value: 38,
    generated_note: 'Platform team closed three more Linear issues. Momentum is holding into the sprint review.',
    records_counted: 38,
  },
  {
    toolkit_slug: 'mixpanel',
    kr_id: 'kr_demo_signups',
    kr_title: 'Weekly product sign-ups',
    goal_title: 'Top of funnel growth',
    trigger: 'scheduled',
    status: 'success',
    previous_value: 1240,
    new_value: 1418,
    generated_note: 'Sign-ups jumped 14% this week. The new onboarding email is showing impact.',
    records_counted: 1418,
  },
  {
    toolkit_slug: 'hubspot',
    kr_id: 'kr_demo_pipeline_q2',
    kr_title: 'Closed-won pipeline value',
    goal_title: 'Hit Q2 revenue target',
    trigger: 'scheduled',
    status: 'success',
    previous_value: 1500000,
    new_value: 1620000,
    unit: 'THB',
    generated_note: 'Pipeline grew by THB 120,000 from three new closed-won deals on the Acme account.',
    records_counted: 22,
  },
  {
    toolkit_slug: 'google_sheets',
    kr_id: 'kr_demo_eng_hours',
    kr_title: 'Engineering review hours logged',
    goal_title: 'Lift code review quality',
    trigger: 'scheduled',
    status: 'error',
    error: { code: 'sheet_not_found', message: '"Eng Hours Q2" sheet not found. Check the sheet name or share access.' },
    records_counted: 0,
  },
  {
    toolkit_slug: 'stripe',
    kr_id: 'kr_demo_mrr',
    kr_title: 'Monthly recurring revenue',
    goal_title: 'Reach USD 250k MRR',
    trigger: 'scheduled',
    status: 'success',
    previous_value: 213100,
    new_value: 218400,
    unit: 'USD',
    generated_note: 'MRR climbed by USD 5,300 this week. Three subscription upgrades drove most of it.',
    records_counted: 309,
  },
];

const seedSyncRuns = (): SyncRun[] => {
  const out: SyncRun[] = [];
  const now = Date.now();
  // Distribute the templates across the past ~7 days, sorted desc.
  SAMPLE_SYNC_TEMPLATES.forEach((tpl, i) => {
    const hoursAgo = i * 11 + (i % 3) * 4 + 1; // 1h, 12h, 27h, 34h, ...
    const startedAt = new Date(now - hoursAgo * 60 * 60 * 1000).toISOString();
    out.push({
      ...tpl,
      id: `sr_demo_${i.toString(36)}_${Math.abs((tpl.kr_id + i).split('').reduce((a, c) => a + c.charCodeAt(0), 0)).toString(36)}`,
      started_at: startedAt,
      duration_ms: 1200 + ((i * 137) % 2400),
    });
  });
  return out;
};

const loadSyncRuns = (): SyncRun[] => {
  try {
    const raw = window.localStorage.getItem(SYNC_RUNS_KEY);
    if (!raw) {
      const seeded = seedSyncRuns();
      window.localStorage.setItem(SYNC_RUNS_KEY, JSON.stringify(seeded));
      return seeded;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const demoListSyncRuns = async (params?: {
  toolkit_slug?: string;
  status?: SyncRunStatus | 'all' | 'failures';
  limit?: number;
}): Promise<{ runs: SyncRun[]; failure_count_24h: number }> => {
  await sleep(220);
  const all = loadSyncRuns().slice().sort((a, b) =>
    (b.started_at || '').localeCompare(a.started_at || '')
  );
  const cutoff24h = Date.now() - 24 * 60 * 60 * 1000;
  const failure_count_24h = all.filter(
    (r) =>
      (r.status === 'error' || r.status === 'auth_expired') &&
      new Date(r.started_at).getTime() >= cutoff24h
  ).length;
  let filtered = all;
  if (params && params.toolkit_slug) {
    filtered = filtered.filter((r) => r.toolkit_slug === params.toolkit_slug);
  }
  if (params && params.status && params.status !== 'all') {
    if (params.status === 'failures') {
      filtered = filtered.filter((r) => r.status === 'error' || r.status === 'auth_expired');
    } else {
      filtered = filtered.filter((r) => r.status === params.status);
    }
  }
  if (params && params.limit) {
    filtered = filtered.slice(0, params.limit);
  }
  return { runs: filtered, failure_count_24h };
};

export const clearDemoSyncRuns = () => {
  try {
    window.localStorage.removeItem(SYNC_RUNS_KEY);
  } catch {
    /* ignore */
  }
};
